import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { Shield, Search, X, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ACOES = ['LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT', 'EXPORT_PDF', 'BULK_UPDATE'];
const COLECOES = [
  'pedidos', 'clientes', 'materiais', 'orcamentos', 'usuarios',
  'notas_fiscais', 'vencimentos', 'metas', 'representadas', 'tenants', 'backup',
];

const ACAO_BADGE = {
  LOGIN:       'bg-blue-100 text-blue-800 border-blue-300',
  CREATE:      'bg-emerald-100 text-emerald-800 border-emerald-300',
  UPDATE:      'bg-amber-100 text-amber-800 border-amber-300',
  DELETE:      'bg-red-100 text-red-700 border-red-300',
  EXPORT:      'bg-purple-100 text-purple-800 border-purple-300',
  EXPORT_PDF:  'bg-purple-100 text-purple-800 border-purple-300',
  BULK_UPDATE: 'bg-orange-100 text-orange-800 border-orange-300',
};

const ROLE_BADGE = {
  admin:      'bg-blue-100 text-blue-700',
  vendedor:   'bg-slate-100 text-slate-600',
  superadmin: 'bg-amber-100 text-amber-700',
};

const fmtDatetime = (iso) => {
  if (!iso) return '---';
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString('pt-BR') +
      ' ' +
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    );
  } catch { return iso; }
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A3D73] transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const ITEMS_PER_PAGE = 50;

const Auditoria = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterEmail, setFilterEmail]     = useState('');
  const [filterAcao, setFilterAcao]       = useState('');
  const [filterColecao, setFilterColecao] = useState('');
  const [filterDe, setFilterDe]           = useState('');
  const [filterAte, setFilterAte]         = useState('');

  const fetchLogs = async (explicitParams = null) => {
    setLoading(true);
    try {
      let params;
      if (explicitParams !== null) {
        params = explicitParams;
      } else {
        params = { limite: 500 };
        if (filterEmail.trim()) params.usuario_email = filterEmail.trim();
        if (filterAcao)         params.acao           = filterAcao;
        if (filterColecao)      params.colecao        = filterColecao;
      }
      const r = await api.get('/lgpd/auditoria', { params });
      setLogs(Array.isArray(r.data) ? r.data : []);
      setCurrentPage(1);
    } catch {
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []); // eslint-disable-line

  const handleBuscar = () => fetchLogs();

  const handleLimpar = () => {
    setFilterEmail('');
    setFilterAcao('');
    setFilterColecao('');
    setFilterDe('');
    setFilterAte('');
    fetchLogs({ limite: 500 });
  };

  const filteredLogs = useMemo(() => logs.filter(log => {
    if (filterDe || filterAte) {
      const dataLog = log.criado_em ? log.criado_em.slice(0, 10) : '';
      if (filterDe && dataLog < filterDe) return false;
      if (filterAte && dataLog > filterAte) return false;
    }
    return true;
  }), [logs, filterDe, filterAte]);

  useEffect(() => { setCurrentPage(1); }, [filterDe, filterAte]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / ITEMS_PER_PAGE));
  const paginated  = filteredLogs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Logs...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0A3D73] flex items-center justify-center shadow-sm">
            <Shield size={16} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900">Log de Auditoria</h1>
            <p className="text-xs text-slate-500 font-semibold mt-0.5">
              Registro completo de ações no sistema · {filteredLogs.length} registros
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchLogs()}
          className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className={lbl}>Usuário (email)</label>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="ex: usuario@email.com"
                value={filterEmail}
                onChange={e => setFilterEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleBuscar()}
                className={`${inp} pl-8`}
              />
            </div>
          </div>
          <div>
            <label className={lbl}>Ação</label>
            <select value={filterAcao} onChange={e => setFilterAcao(e.target.value)} className={inp}>
              <option value="">Todas as ações</option>
              {ACOES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Coleção</label>
            <select value={filterColecao} onChange={e => setFilterColecao(e.target.value)} className={inp}>
              <option value="">Todas as coleções</option>
              {COLECOES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Data — De</label>
            <input type="date" value={filterDe} onChange={e => setFilterDe(e.target.value)} className={inp} />
          </div>
          <div>
            <label className={lbl}>Data — Até</label>
            <input type="date" value={filterAte} onChange={e => setFilterAte(e.target.value)} className={inp} />
          </div>
        </div>
        <div className="flex gap-2 mt-3 justify-end">
          <button
            onClick={handleLimpar}
            className="flex items-center gap-1.5 px-4 h-8 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
            <X size={12} /> Limpar filtros
          </button>
          <button
            onClick={handleBuscar}
            className="flex items-center gap-1.5 px-4 h-8 rounded-lg bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase transition-colors">
            <Search size={12} /> Buscar
          </button>
        </div>
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                <th className="px-4 py-3 whitespace-nowrap">Data / Hora</th>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3 text-center">Ação</th>
                <th className="px-4 py-3">Coleção</th>
                <th className="px-4 py-3">Documento ID</th>
                <th className="px-4 py-3">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Shield size={32} className="text-slate-200 mx-auto mb-3" strokeWidth={1} />
                    <p className="text-sm font-bold text-slate-400">Nenhum registro encontrado</p>
                    <p className="text-xs text-slate-300 mt-1">Ajuste os filtros e tente novamente</p>
                  </td>
                </tr>
              ) : paginated.map((log, idx) => {
                const badgeCls = ACAO_BADGE[log.acao] || 'bg-slate-100 text-slate-600 border-slate-200';
                const roleCls  = ROLE_BADGE[log.usuario_role] || 'bg-slate-100 text-slate-500';
                const docId    = log.documento_id || '—';
                const docTrunc = docId.length > 18 ? docId.slice(0, 16) + '…' : docId;
                return (
                  <tr key={log._id || idx}
                    className={`text-xs border-b border-slate-100 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                    <td className="px-4 py-2.5 font-mono text-slate-500 whitespace-nowrap">
                      {fmtDatetime(log.criado_em)}
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-slate-700 truncate max-w-[200px]">{log.usuario_email || '—'}</p>
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-black uppercase rounded-full mt-0.5 ${roleCls}`}>
                        {log.usuario_role || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 text-[9px] font-black uppercase border rounded-full whitespace-nowrap ${badgeCls}`}>
                        {log.acao || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-slate-600 whitespace-nowrap">
                      {log.colecao || '—'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-500 whitespace-nowrap" title={docId}>
                      {docTrunc}
                    </td>
                    <td className="px-4 py-2.5 max-w-[280px]">
                      <span
                        className="block truncate text-slate-500 cursor-default"
                        title={log.detalhes || '—'}>
                        {log.detalhes || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PAGINAÇÃO */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
            <span className="text-xs font-bold text-slate-500">{filteredLogs.length} registros</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs font-black text-slate-600">
                Página {currentPage} de {totalPages} · {filteredLogs.length} registros
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Auditoria;
